import React from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { useNavigate, useLocation } from 'react-router-dom';
import InstitutionEndPoint from 'endPoints/institutions';
import CoursesEndpoint from 'endPoints/courses';
import { styled } from '@mui/system';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import FirstCard from 'assets/icons/Atom.svg?react';
import SecondCard from 'assets/icons/EyeSore.svg?react';
import ThirdCard from 'assets/icons/EyeSore2.svg?react';
import BgLeft from 'assets/icons/download5.svg?react';
import BgRight from 'assets/icons/download6.svg?react';
import TrollGreetings from 'assets/images/careerTest/troll-greetings.png';
import config from 'config';
import { getLocalizedPath } from 'helpers/getLocale';
const Helmet = React.lazy(() => import('components/Helmet'));
const Header = React.lazy(() => import('components/Header'));
const Footer = React.lazy(() => import('components/Footer'));
const MainLayout = React.lazy(() => import('layouts/MainLayout'));
const Breadcrumbs = React.lazy(() => import('components/BreadCrumbs'));
const List = React.lazy(() => import('./components/List'));
const CoursesList = React.lazy(() => import('./components/Courses'));

const Background = styled('section')(({ theme }) => ({
  backgroundColor: theme.palette.tertiary.pale,
  position: 'relative'
}));

const BgLeftStyled = styled(BgLeft)(({ theme }) => ({
  position: 'absolute',
  left: 0,
  top: 0,
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const TrollGreetingsStyled = styled('img')(({ theme }) => ({
  position: 'absolute',
  bottom: -100,
  right: 0,
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const TrollGreetingsStyledTop = styled('img')(({ theme }) => ({
  position: 'absolute',
  top: 70,
  right: -120,
  transform: 'rotate(-45deg)',
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const BgRightStyled = styled(BgRight)(({ theme }) => ({
  position: 'absolute',
  right: 0,
  bottom: 0,
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const Content = styled('div')(({ theme }) => ({
  ...theme.wrapper,
  maxWidth: 1240,
  paddingTop: 30,
  paddingBottom: 56,
  position: 'relative',
  overflow: 'hidden',
  zIndex: 1,
  [theme.breakpoints.down('lg')]: {
    paddingLeft: 20,
    paddingRight: 20
  },
  [theme.breakpoints.down('sm')]: {
    paddingBottom: 40
  }
}));

const Title = styled(Typography)(({ theme }) => ({
  marginBottom: 20,
  marginTop: 32,
  color: theme.palette.textPrimary.main,
  display: 'block',
  '& span': {
    display: 'block',
    fontFamily: 'Rubik-Regular',
    fontSize: 14,
    fontWeight: 500
  },
  [theme.breakpoints.down('lg')]: {
    display: 'block'
  },
  [theme.breakpoints.down('md')]: {
    fontSize: 36,
    lineHeight: '41.4px',
    marginBottom: 20
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: 28,
    lineHeight: '36.4px',
    marginBottom: 25
  }
}));

const Headline = styled(Typography)(() => ({
  fontSize: 18,
  fontWeight: 600,
  lineHeight: '20px',
  marginBottom: 10
}));

const Text = styled(Typography)(({ mb }) => ({
  fontSize: 16,
  lineHeight: '20px',
  marginBottom: mb || 10,
  flex: 1
}));

const Disclaimer = styled('div')(({ theme }) => ({
  borderRadius: 18,
  backgroundColor: theme.palette.white.main,
  padding: '24px 32px',
  marginBottom: 56,
  position: 'relative',
  overflow: 'hidden',
  paddingRight: 200,
  [theme.breakpoints.down('sm')]: {
    paddingRight: 24
  }
}));

const DisclaimerHeadline = styled(Typography)(({ theme }) => ({
  fontSize: 24,
  lineHeight: '30px',
  fontWeight: 600,
  marginBottom: 12,
  fontFamily: 'Unbounded',
  [theme.breakpoints.down('sm')]: {
    fontSize: 20,
    lineHeight: '25px'
  }
}));

const DisclaimerText = styled(Typography)(() => ({
  fontSize: 16,
  lineHeight: '20px',
  marginBottom: 24
}));

const ButtonStyled = styled(Button)(({ fontSize, width }) => ({
  borderRadius: 8,
  fontSize,
  padding: '10px 31px',
  width
}));

const ButtonOutlined = styled(Button)(({ fontSize, width }) => ({
  borderRadius: 8,
  fontSize,
  padding: '10px 31px',
  width,
  marginTop: 12
}));

const ButtonsWrapper = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
  marginTop: 'auto'
}));

const Cards = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'stretch',
  gap: 24,
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    marginBottom: 20
  }
}));

const Card = styled('div')(({ theme }) => ({
  borderRadius: 18,
  backgroundColor: theme.palette.white.main,
  padding: '32px 48px',
  marginBottom: 56,
  flex: 1,
  position: 'relative',
  overflow: 'hidden',
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.down('sm')]: {
    padding: '24px 16px',
    marginBottom: 0
  }
}));

const Icon = styled('div')(() => ({
  position: 'absolute',
  top: 0,
  right: 0
}));

const MaxWidth = styled('div')(() => ({
  maxWidth: 800
}));

const TrollBackground = styled('img')(({ theme }) => ({
  position: 'absolute',
  width: 228,
  height: 228,
  right: 30,
  bottom: -65,
  [theme.breakpoints.down('sm')]: {
    display: 'none'
  }
}));

const TYPES = {
  universities: 'university',
  colleges: 'college',
  courses: 'course'
};

const EducationProgramsPage = () => {
  const helmetTranslate = useTranslate('HELMET');
  const t = useTranslate('University');
  const [chosen, setChosen] = React.useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  const handleBack = React.useCallback(() => {
    setChosen(null);
  }, []);

  const PageHeadline = React.useCallback(() => {
    return (
      <Title variant="h1">
        {t(chosen)}
        <span>{t(`${chosen}Helper`)}</span>
      </Title>
    );
  }, [chosen, t]);

  const breadcrumbs = React.useMemo(() => {
    const items = [
      { link: '/', label: 'Home' },
      { link: '/education', label: 'EducationProgramsPage', callback: handleBack }
    ];

    if (chosen) {
      items.push({ link: '/education', label: chosen });
    }

    return items;
  }, [chosen, handleBack]);

  React.useEffect(() => {
    const pathname = location.pathname.split('/').filter(Boolean);
    const lastSegment = pathname[pathname.length - 1];
    if (TYPES[lastSegment]) {
      setChosen(TYPES[lastSegment]);
    }
  }, [location]);

  return (
    <>
      <Helmet
        meta={{
          title: helmetTranslate('educationProgramsTitle'),
          description: helmetTranslate('educationProgramsDescription'),
          ogTitle: helmetTranslate('educationProgramsOgTitle'),
          ogDescription: helmetTranslate('educationProgramsOgDescription')
        }}
      />
      <MainLayout>
        <Header />
        <Background>
          <Content>
            <Breadcrumbs breadcrumbs={breadcrumbs} />

            {!chosen ? (
              <>
                <Title variant="h1">{t('Title')}</Title>

                <Cards>
                  <Card>
                    <Icon>
                      <FirstCard />
                    </Icon>
                    <DisclaimerHeadline>{t('university')}</DisclaimerHeadline>
                    <Text mb={24}>{t('universityHeadline')}</Text>

                    <ButtonsWrapper>
                      <ButtonStyled
                        onClick={() => navigate(getLocalizedPath('/education/universities'))}
                        variant="contained"
                        fontSize={16}
                        width={'100%'}>
                        {t('searchUniversity')}
                      </ButtonStyled>
                      <ButtonOutlined
                        onClick={() => navigate(getLocalizedPath('/education/universities/programs'))}
                        variant="outlined"
                        fontSize={14}
                        width={'100%'}>
                        {t('searchPrograms')}
                      </ButtonOutlined>
                    </ButtonsWrapper>
                  </Card>

                  <Card>
                    <Icon>
                      <SecondCard />
                    </Icon>
                    <DisclaimerHeadline>{t('college')}</DisclaimerHeadline>
                    <Text mb={24}>{t('collegeHeadline')}</Text>

                    <ButtonsWrapper>
                      <ButtonStyled
                        onClick={() => navigate(getLocalizedPath('/education/colleges'))}
                        variant="contained"
                        fontSize={16}
                        width={'100%'}>
                        {t('searchCollege')}
                      </ButtonStyled>
                      <ButtonOutlined
                        onClick={() => navigate(getLocalizedPath('/education/colleges/programs'))}
                        variant="outlined"
                        fontSize={14}
                        width={'100%'}>
                        {t('searchPrograms')}
                      </ButtonOutlined>
                    </ButtonsWrapper>
                  </Card>

                  <Card>
                    <Icon>
                      <ThirdCard />
                    </Icon>
                    <DisclaimerHeadline>{t('course')}</DisclaimerHeadline>
                    <Text mb={24}>{t('courseHeadline')}</Text>

                    <ButtonsWrapper>
                      <ButtonStyled
                        onClick={() => navigate(getLocalizedPath('/education/platforms'))}
                        variant="contained"
                        fontSize={16}
                        width={'100%'}>
                        {t('searchPlatform')}
                      </ButtonStyled>
                      <ButtonOutlined
                        onClick={() => navigate(getLocalizedPath('/education/platforms/courses'))}
                        variant="outlined"
                        fontSize={14}
                        width={'100%'}>
                        {t('searchCourse')}
                      </ButtonOutlined>
                    </ButtonsWrapper>
                  </Card>
                </Cards>

                <Disclaimer>
                  <DisclaimerHeadline>{t('testTitle')}</DisclaimerHeadline>
                  <DisclaimerText>{t('testText')}</DisclaimerText>

                  <ButtonStyled
                    variant="contained"
                    fontSize={14}
                    onClick={() => navigate(getLocalizedPath('/assessment'))}>
                    {t('testAction')}
                  </ButtonStyled>

                  <TrollBackground
                    alt="troll"
                    src={`${config.imageSource}/904a0ae0-052c-49d2-c0e1-ae652f565800/w285h325`}
                  />
                </Disclaimer>

                <MaxWidth>
                  <Headline>{t('disclaimerTitle')}</Headline>
                  <Text>{t('disclaimerText')}</Text>
                  <Text>{t('disclaimerText2')}</Text>
                </MaxWidth>
              </>
            ) : (
              <>
                {chosen === 'course' ? (
                  <>
                    <PageHeadline />

                    <CoursesList
                      endPoint={CoursesEndpoint}
                      translate={'University'}
                      chosen={chosen}
                    />
                  </>
                ) : (
                  <List
                    endPoint={InstitutionEndPoint()}
                    translate={'University'}
                    chosen={chosen}
                    headline={<PageHeadline />}
                  />
                )}

                <MaxWidth>
                  {chosen === 'university' ? (
                    <>
                      <Headline>{t('helperTitle')}</Headline>
                      <Text>{t('helperText')}</Text>
                      <Text>{t('helperText1')}</Text>
                    </>
                  ) : null}
                  {chosen === 'college' ? (
                    <>
                      <Headline>{t('helperCollegeTitle')}</Headline>
                      <Text>{t('helperCollegeText')}</Text>
                      <Text>{t('helperCollegeText1')}</Text>
                    </>
                  ) : null}
                  {chosen === 'course' ? (
                    <>
                      <Headline>{t('helperCourseTitle')}</Headline>
                      <Text>{t('helperCourseText')}</Text>
                      <Text>{t('helperCourseText1')}</Text>
                    </>
                  ) : null}
                </MaxWidth>
              </>
            )}
            <TrollGreetingsStyled src={TrollGreetings} alt="" />
          </Content>
          {chosen ? <TrollGreetingsStyledTop src={TrollGreetings} alt="" /> : null}
          <BgLeftStyled />
          <BgRightStyled />
        </Background>
        <Footer />
      </MainLayout>
    </>
  );
};

export default EducationProgramsPage;
